import React from "react";
import {
  Steps,
  Button,
  Select,
  Form,
  Input,
  Switch,
  Tag,
  Space,
  message as antMessage,
  Card,
  Row,
  Col,
  Typography,
  Divider,
  Upload,
  Spin
} from "antd";
import {
  PageContainer,
  PageHeader,
  PageContent,
} from "../../components/Layout";
import { useHistory } from "react-router-dom";
import { useTranslate } from "../../hooks";
import { order as orderApi, property as propertyApi, user as userApi, business as businessApi } from "../../apis";
import { FolderOpenOutlined, FileOutlined, DeleteOutlined } from "@ant-design/icons";
import moment from "moment";
import styled from "styled-components";
import NewCalendarForm from "../../components/Calendar/NewCalendarForm";

const { Step } = Steps;
const { Option } = Select;
const { Title, Text } = Typography;

export default () => {
  const { translate } = useTranslate();
  const history = useHistory();

  const [current, setCurrent] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  const [selectedUser, setSelectedUser] = React.useState(null);
  const [selectedProperty, setSelectedProperty] = React.useState(null);
  const [selectedDateRange, setSelectedDateRange] = React.useState([]);
  const [disabledDates, setDisabledDates] = React.useState([]);
  const [gahaiError, setGahaiError] = React.useState([false, false]);

  const [form] = Form.useForm();
  const [businessList, setBusinessList] = React.useState([]);
  const [businessLoading, setBusinessLoading] = React.useState(false);
  const [selectedBusiness, setSelectedBusiness] = React.useState(null);
  const [uploadedFile, setUploadedFile] = React.useState(null);
  const [fileUploading, setFileUploading] = React.useState(false);

  React.useEffect(() => {
    setBusinessLoading(true);
    businessApi
      .list()({ offset: { page: 1, limit: 100 } })
      .then((res) => setBusinessList(res?.rows || []))
      .catch(() => {})
      .finally(() => setBusinessLoading(false));
  }, []);
  React.useEffect(() => {
    if (!selectedUser) return;
    setBusinessLoading(true);
    setBusinessList([]);
    setSelectedBusiness(null);
    businessApi
      .list()({ offset: { page: 1, limit: 100 }, userId: selectedUser._id })
      .then((res) => setBusinessList(res?.rows || []))
      .catch(() => {})
      .finally(() => setBusinessLoading(false));
  }, [selectedUser]);
  console.log(selectedUser);
  const goStep1 = () => {
    if (!selectedUser) {
      antMessage.error("ユーザーを選択してください");
      return;
    }
    setCurrent(1);
  };

  const onPropertySelected = async (prop) => {
    setSelectedDateRange([]);
    setDisabledDates([]);
    setGahaiError([false, false]);
    if (!prop) {
      setSelectedProperty(null);
      return;
    };
    try {
      const res = await propertyApi.get(prop._id);
      setSelectedProperty(res);
      if (res?.unavailableDates) {
        setDisabledDates(
          res.unavailableDates.map((d) => moment(d).format("YYYY/MM/DD"))
        );
      }
    } catch (err) {
      console.log(err);
    }
  };

  const goStep2 = () => {
    if (!selectedProperty) {
      antMessage.error("スペースを選択してください");
      return;
    }
    if (selectedDateRange.length === 0) {
      antMessage.error("利用日程を選択してください");
      return;
    }
    if (gahaiError[0]) {
      antMessage.error("利用不可の日付が含まれています");
      return;
    }
    setCurrent(2);
  };

  const onBusinessSelect = (id) => {
    const biz = businessList.find((b) => b._id === id);
    if (!biz) return;
    setSelectedBusiness(biz);
    form.setFieldsValue({
      useType      : biz.useType || "",
      businessName : biz.businessName || "",
      businessDesc : biz.businessDesc || "",
      inquiry      : biz.inquiry || "",
      homepage     : biz.homepage || "",
      isProvisional: !!biz.isProvisional,
    });
    if (biz.files && biz.files.length > 0) {
      setUploadedFile(biz.files[0]);
    } else {
      setUploadedFile(null);
    }
  };
  const customUpload = async ({ file, onSuccess, onError }) => {
    setFileUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch("/api/general/upload", { method: "POST", body: formData });
      const json = await res.json();
      setUploadedFile({ url: json.url, description: json.fileName || file.name });
      onSuccess(json);
    } catch (err) {
      onError(err);
      antMessage.error("ファイルのアップロードに失敗しました");
    } finally {
      setFileUploading(false);
    }
  };
  const onFinish = async (values) => {
    setLoading(true);
    try {
      await orderApi.adminCreate({
        userId       : selectedUser._id,
        propertyId   : selectedProperty._id,
        dates        : selectedDateRange,
        useType      : values.useType,
        businessName : values.businessName,
        businessDesc : values.businessDesc,
        inquiry      : values.inquiry,
        homepage     : values.homepage || "",
        isProvisional: values.isProvisional || false,
        files        : uploadedFile ? [uploadedFile] : [],
      });
      antMessage.success("注文が作成されました");
      history.push("/chat");
    } catch (err) {
      antMessage.error(err?.message || "Failed");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    translate("admin.order.step.user", "ユーザー選択"),
    translate("admin.order.step.space", "スペース・日程"),
    translate("admin.order.step.inquiry", "問い合わせ内容"),
  ];

  return (
    <PageContainer>
      <PageHeader
        title={translate("admin.order.create.title", "注文作成")}
        onBack={() => history.push("/chat")}
      />
      <PageContent>
        <StepsWrapper>
          <Steps current={current} style={{ marginBottom: 32 }}>
            {steps.map((s) => <Step key={s} title={s} />)}
          </Steps>
        </StepsWrapper>
        {current === 0 && (
          <StepCard>
            <Title level={5} style={{ marginBottom: 16 }}>
              {translate("admin.order.select.user", "ユーザーを選択してください")}
            </Title>
            <UserSelector
              onSelect={setSelectedUser}
              selected={selectedUser}
              translate={translate}
            />
            <Divider />
            <Row justify="end">
              <Button type="primary" onClick={goStep1}>
                {translate("system.btn.next", "次へ")}
              </Button>
            </Row>
          </StepCard>
        )}

        {current === 1 && (
          <StepCard>
            <Title level={5} style={{ marginBottom: 16 }}>
              {translate("admin.order.select.property", "スペースと日程を選択してください")}
            </Title>
            <PropertySelector
              onSelect={onPropertySelected}
              selected={selectedProperty}
              translate={translate}
            />

            {selectedProperty && (
              <>
                <Divider />
                <Title level={5} style={{ marginBottom: 8 }}>
                  {translate("admin.order.select.date", "利用日程")}
                </Title>

                <NewCalendarForm
                  inquireDates={selectedProperty?.inquireDates || []}
                  minRange={selectedProperty?.minimumDay}
                  minRequired={selectedProperty?.minRequired}
                  gahaiError={setGahaiError}
                  setDisable={setDisabledDates}
                  setRange={setSelectedDateRange}
                  owner={false}
                  disabledDates={disabledDates}
                />

                {selectedDateRange.length > 0 && (
                  <SelectedList>
                    {selectedDateRange.map((item, idx) => (
                      <SelectedItem key={idx}>
                        <span>
                          {item.startDate === item.endDate
                            ? item.startDate
                            : `${item.startDate} — ${item.endDate}`}
                        </span>
                        <Button
                          size="small"
                          type="text"
                          danger
                          onClick={() => {
                            const updatedRanges = selectedDateRange.filter((_, i) => i !== idx);

                            setSelectedDateRange(updatedRanges);

                            const rebuiltDisabledDates = updatedRanges.flatMap((range) => {
                              const start = moment(range.startDate, "YYYY/MM/DD");
                              const end = moment(range.endDate, "YYYY/MM/DD");

                              const days = [];
                              let current = start.clone();

                              while (current.isSameOrBefore(end)) {
                                days.push(current.format("YYYY/MM/DD"));
                                current.add(1, "day");
                              }

                              return days;
                            });

                            setDisabledDates(rebuiltDisabledDates);
                          }}
                        >
                          削除
                        </Button>
                      </SelectedItem>
                    ))}
                  </SelectedList>
                )}

                {gahaiError[0] && (
                  <ErrorBox>利用不可の日付が含まれています。別の日程を選択してください。</ErrorBox>
                )}
                {gahaiError[1] && !gahaiError[0] && (
                  selectedProperty?.minRequired
                    ? <ErrorBox>最低 {selectedProperty.minimumDay} 日以上選択してください。</ErrorBox>
                    : <WarnBox>最低 {selectedProperty?.minimumDay} 日以上の選択を推奨します。</WarnBox>
                )}
              </>
            )}

            <Divider />
            <Row justify="space-between">
              <Button onClick={() => setCurrent(0)}>
                {translate("system.btn.prev", "戻る")}
              </Button>
              <Button type="primary" onClick={goStep2}>
                {translate("system.btn.next", "次へ")}
              </Button>
            </Row>
          </StepCard>
        )}

        {current === 2 && (
          <StepCard>
            <Title level={5} style={{ marginBottom: 16 }}>
              {translate("admin.order.inquiry.title", "問い合わせ内容を入力してください")}
            </Title>
            <SummaryBox
              user={selectedUser}
              property={selectedProperty}
              dateRanges={selectedDateRange}
              translate={translate}
            />
            <Divider />
            <div style={{ marginBottom: 16 }}>
              <Title level={5} style={{ marginBottom: 4 }}>
                {translate("app.bulk.step2.select.title", "問い合わせ/企画を選択して自動入力")}
              </Title>
              <Text type="secondary" style={{ display: "block", marginBottom: 8, fontSize: 13 }}>
                選択すると下のフォームが自動的に入力されます。手動で編集も可能です。
              </Text>
              <Select
                showSearch
                allowClear
                loading={businessLoading}
                style={{ width: "100%" }}
                placeholder={translate("app.bulk.step2.select.placeholder", "問い合わせ/企画を選択...")}
                filterOption={(input, option) =>
                  (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
                }
                onChange={(id) => id && onBusinessSelect(id)}
                value={selectedBusiness?._id}
                size="large"
              >
                {businessList.map((b) => (
                  <Option key={b._id} value={b._id}>
                    {b.name || b.businessName}
                  </Option>
                ))}
              </Select>
            </div>
            <Divider style={{ marginTop: 8 }} />
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              initialValues={{ isProvisional: false }}
            >
              <Form.Item
                name="useType"
                label={translate("app.bulk.step3.useType", "利用目的")}
                rules={[{ required: true, message: "利用目的を入力してください" }]}
              >
                <Input placeholder="例：展示会、撮影、ポップアップショップ" />
              </Form.Item>

              <Form.Item
                name="businessName"
                label={translate("app.bulk.step3.businessName", "企画名")}
                rules={[{ required: true, message: "企画名を入力してください" }]}
              >
                <Input placeholder="企画名" />
              </Form.Item>

              <Form.Item
                name="businessDesc"
                label={translate("app.bulk.step3.businessDesc", "企画内容")}
                rules={[{ required: true, message: "企画内容を入力してください" }]}
              >
                <Input.TextArea rows={4} placeholder="企画内容の詳細" />
              </Form.Item>

              <Form.Item
                name="inquiry"
                label={translate("app.bulk.step3.inquiry", "スペースへのお問い合わせ内容")}
                rules={[{ required: true, message: "お問い合わせ内容を入力してください" }]}
              >
                <Input.TextArea rows={4} placeholder="スペースへのお問い合わせ内容" />
              </Form.Item>

              <Form.Item
                name="homepage"
                label={translate("app.bulk.step3.homepage", "ホームページ（任意）")}
              >
                <Input placeholder="https://..." />
              </Form.Item>

              <Form.Item
                name="isProvisional"
                label={translate("app.bulk.step3.isProvisional", "飲食/試食 提供の有無")}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Form.Item label={translate("app.bulk.step1.files", "企画に関する資料")}>
                <Spin spinning={fileUploading}>
                  {uploadedFile ? (
                    <FilePreviewBox>
                      <FileOutlined style={{ fontSize: 22, color: "#f97316" }} />
                      <div style={{ flex: 1, fontSize: 14, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                        <a href={uploadedFile.url} target="_blank" rel="noreferrer"
                          style={{ color: "#146cbe", textDecoration: "underline" }}>
                          {uploadedFile.description}
                        </a>
                      </div>
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => setUploadedFile(null)} />
                    </FilePreviewBox>
                  ) : (
                    <Upload customRequest={customUpload} showUploadList={false} maxCount={1}>
                      <UploadArea>
                        <FolderOpenOutlined style={{ fontSize: 28, color: "#c0c6cf" }} />
                        <div style={{ fontSize: 14, color: "#9ba1ab" }}>
                          {translate("ticket.file.upload", "Drag and drop, or browse your file")}
                        </div>
                      </UploadArea>
                    </Upload>
                  )}
                </Spin>
              </Form.Item>
              <Divider />
              <Row justify="space-between">
                <Button onClick={() => setCurrent(1)} disabled={loading}>
                  {translate("system.btn.prev", "戻る")}
                </Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  {translate("admin.order.create.submit", "問い合わせを作成する")}
                </Button>
              </Row>
            </Form>
          </StepCard>
        )}
      </PageContent>
    </PageContainer>
  );
};

const UserSelector = ({ onSelect, selected, translate }) => {
  const [query, setQuery] = React.useState("");
  const [users, setUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const debounce = React.useRef();

  React.useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await userApi.list("orderCreate")({
          filter: { query },
          offset: { page: 1, limit: 30 },
        });
        setUsers(Array.isArray(res?.rows) ? res.rows : []);
      } catch (err) {
        console.log(err);
        setUsers([]);
      }
      setLoading(false);
    }, 300);
  }, [query]);

  return (
    <Select
      showSearch
      style={{ width: "100%" }}
      placeholder={translate("admin.order.user.placeholder", "ユーザー名、メール、IDで検索")}
      filterOption={false}
      loading={loading}
      onSearch={setQuery}
      onChange={(_, option) => onSelect(option?.data || null)}
      value={selected?._id}
      allowClear
      onClear={() => onSelect(null)}
      size="large"
    >
      {Array.isArray(users) && users.map((u) => (
        <Option key={u._id} value={u._id} data={u}>
          <Space>
            <Tag color="blue">{u.userId}</Tag>
            <span style={{ fontWeight: 600 }}>
              {u.type === "PERSONAL"
                ? `${u.lastName || ""} ${u.firstName || ""}`
                : u.orgname || ""}
            </span>
            <Text type="secondary" style={{ fontSize: 12 }}>{u.email}</Text>
          </Space>
        </Option>
      ))}
    </Select>
  );
};

const PropertySelector = ({ onSelect, selected, translate }) => {
  const [query, setQuery] = React.useState("");
  const [allProperties, setAll] = React.useState([]);
  const [properties, setProperties] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    propertyApi.selectAdminActives()
      .then((res) => { setAll(res || []); setProperties(res || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    setProperties(
      !query
        ? allProperties
        : allProperties.filter((p) =>
          (p.name || p.title || "").toLowerCase().includes(query.toLowerCase())
        )
    );
  }, [query, allProperties]);

  return (
    <Select
      showSearch
      style={{ width: "100%" }}
      placeholder={translate("admin.order.property.placeholder", "スペース名で検索")}
      filterOption={false}
      loading={loading}
      onSearch={setQuery}
      onChange={(_, option) => onSelect(option?.data || null)}
      value={selected?._id}
      allowClear
      onClear={() => onSelect(null)}
      size="large"
    >
      {properties.map((p) => (
        <Option key={p._id} value={p._id} data={p}>
          <Space>
            <span style={{ fontWeight: 600 }}>{p.name || p.title}</span>
            {p.propertyStatus && <Tag>{p.propertyStatus}</Tag>}
          </Space>
        </Option>
      ))}
    </Select>
  );
};


const SummaryBox = ({ user, property, dateRanges, translate }) => (
  <Card size="small" style={{ marginBottom: 16, background: "#fafafa" }}>
    <Row gutter={[16, 8]}>
      <Col span={24}>
        <Text type="secondary">{translate("admin.order.summary.user", "ユーザー")}: </Text>
        <Tag color="blue">{user?.userId}</Tag>
        <Text strong>
          {user?.type === "PERSONAL"
            ? `${user?.lastName || ""} ${user?.firstName || ""}`
            : user?.orgname || ""}
        </Text>
        <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>{user?.email}</Text>
      </Col>
      <Col span={24}>
        <Text type="secondary">{translate("admin.order.summary.space", "スペース")}: </Text>
        <Text strong>{property?.name || property?.title}</Text>
      </Col>
      <Col span={24}>
        <Text type="secondary">{translate("admin.order.summary.date", "日程")}: </Text>
        {dateRanges.map((d, i) => (
          <Tag key={i} color="orange" style={{ marginBottom: 4 }}>
            {d.startDate === d.endDate ? d.startDate : `${d.startDate} — ${d.endDate}`}
          </Tag>
        ))}
      </Col>
    </Row>
  </Card>
);

const StepsWrapper = styled.div`
  max-width: 700px;
  margin: 0 auto;
`;

const StepCard = styled(Card)`
  max-width: 700px;
  margin: 0 auto;
  border-radius: 8px;
`;

const SelectedList = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  padding: 8px 12px;
`;

const SelectedItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #fff;
  font-weight: 500;
`;

const ErrorBox = styled.div`
  margin-top: 10px;
  padding: 8px 12px;
  background: #fff1f0;
  border: 1px solid #ffa39e;
  border-radius: 4px;
  color: #cf1322;
  font-size: 13px;
`;

const WarnBox = styled.div`
  margin-top: 10px;
  padding: 8px 12px;
  background: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 4px;
  color: #d46b08;
  font-size: 13px;
`;

const UploadArea = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 14px 20px;
  background: #fff;
  cursor: pointer;
  &:hover { border-color: #f97316; }
`;

const FilePreviewBox = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  border: 2px dashed #f97316;
  border-radius: 8px;
  padding: 12px 20px;
  background: #fff;
`;